/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useState } from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
} from "@mui/material";
import { UserActivitySummary } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { NUMBER_FORMATTER } from "../utils/utils";
import styled from "@emotion/styled";

const LeaderboardContainer = styled.div`
  height: 400px;
  overflow-y: auto;
`;

const RankCell = styled(TableCell)`
  width: 60px;
  text-align: center;
`;

const UserCell = styled(TableCell)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

interface UserLeaderboardProps {
  userActivitySummary: UserActivitySummary[];
  isLoading: boolean;
}

type LeaderboardMetric =
  | "total_changes"
  | "approval_rate"
  | "duplicates_processed";

export const UserLeaderboard: React.FC<UserLeaderboardProps> = ({
  userActivitySummary,
  isLoading,
}) => {
  const [selectedMetric, setSelectedMetric] =
    useState<LeaderboardMetric>("total_changes");

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return rank.toString();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const sortUsers = (metric: LeaderboardMetric) => {
    return [...userActivitySummary].sort((a, b) => {
      switch (metric) {
        case "total_changes":
          return b.total_changes - a.total_changes;
        case "approval_rate":
          const aRate =
            a.total_changes > 0 ? a.approved_changes / a.total_changes : 0;
          const bRate =
            b.total_changes > 0 ? b.approved_changes / b.total_changes : 0;
          return bRate - aRate;
        case "duplicates_processed":
          const aDuplicates = Number.isNaN(a.duplicates_processed)
            ? 0
            : a.duplicates_processed;
          const bDuplicates = Number.isNaN(b.duplicates_processed)
            ? 0
            : b.duplicates_processed;
          return bDuplicates - aDuplicates;
        default:
          return 0;
      }
    });
  };

  const getMetricValue = (
    user: UserActivitySummary,
    metric: LeaderboardMetric
  ) => {
    switch (metric) {
      case "total_changes":
        return NUMBER_FORMATTER.format(user.total_changes);
      case "approval_rate":
        const rate =
          user.total_changes > 0
            ? (user.approved_changes / user.total_changes) * 100
            : 0;
        return `${Math.round(rate)}%`;
      case "duplicates_processed":
        return NUMBER_FORMATTER.format(
          Number.isNaN(user.duplicates_processed)
            ? 0
            : user.duplicates_processed
        );
      default:
        return "0";
    }
  };

  const getMetricLabel = (metric: LeaderboardMetric) => {
    switch (metric) {
      case "total_changes":
        return "Total Changes";
      case "approval_rate":
        return "Approval Rate";
      case "duplicates_processed":
        return "Duplicates Processed";
      default:
        return "";
    }
  };

  const getPerformanceColor = (user: UserActivitySummary, rank: number) => {
    if (rank <= 3) return "success";
    if (selectedMetric === "approval_rate") {
      const rate =
        user.total_changes > 0 ? user.approved_changes / user.total_changes : 0;
      if (rate > 0.8) return "success";
      if (rate > 0.6) return "warning";
      return "error";
    }
    return "default";
  };

  // Inactive users are listed after active ones and are not ranked
  const sortedUsers = sortUsers(selectedMetric);
  const activeUsers = sortedUsers.filter((user) => user.disabled !== true);
  const inactiveUsers = sortedUsers.filter((user) => user.disabled === true);
  const rankedUsers = [...activeUsers, ...inactiveUsers];

  return (
    <>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid>
          <Typography variant="h4" gutterBottom>
            User Leaderboard
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MiraCard>
              <CardHeader
                title="Top Performers"
                action={
                  <ToggleButtonGroup
                    value={selectedMetric}
                    exclusive
                    onChange={(_, newMetric) =>
                      newMetric && setSelectedMetric(newMetric)
                    }
                    size="small"
                  >
                    <ToggleButton value="total_changes">Changes</ToggleButton>
                    <ToggleButton value="approval_rate">Approval</ToggleButton>
                    <ToggleButton value="duplicates_processed">
                      Duplicates
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              />
              <MiraCardContent>
                <LeaderboardContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <RankCell>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Rank
                          </Typography>
                        </RankCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            User
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            {getMetricLabel(selectedMetric)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Status
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankedUsers.map((user, index) => {
                        const isInactive = user.disabled === true;
                        const rank = index + 1;
                        const isTopThree = !isInactive && rank <= 3;
                        return (
                          <TableRow key={user.user_id}>
                            <RankCell>
                              <Typography
                                variant="h6"
                                style={{ fontSize: "1.1rem" }}
                              >
                                {isInactive ? "–" : getRankIcon(rank)}
                              </Typography>
                            </RankCell>
                            <UserCell component="th" scope="row">
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: isTopThree
                                    ? "primary.main"
                                    : "grey.400",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {getInitials(user.user_name)}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: isTopThree ? "600" : "500",
                                  }}
                                >
                                  {user.user_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {NUMBER_FORMATTER.format(user.total_changes)}{" "}
                                  total changes
                                </Typography>
                              </Box>
                            </UserCell>
                            <TableCell align="right">
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: isTopThree ? "600" : "500",
                                  fontSize: isTopThree ? "1.1rem" : "1rem",
                                }}
                              >
                                {getMetricValue(user, selectedMetric)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {isInactive ? (
                                <Chip
                                  size="small"
                                  label="Inactive"
                                  color="default"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label={rank <= 3 ? "Top Performer" : "Active"}
                                  color={getPerformanceColor(user, rank)}
                                  variant={rank <= 3 ? "filled" : "outlined"}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </LeaderboardContainer>
              </MiraCardContent>
            </MiraCard>
          )}
        </Grid>
      </Grid>
    </>
  );
};
